import * as React from "react";

import { cn } from "../lib/utils";

interface PageProps extends React.HTMLAttributes<HTMLDivElement> {
  width?: "narrow" | "default" | "wide";
}

/**
 * Page is the standard wrapper used by every routed page.
 *
 * It fills the available vertical space inside the AppShell, owns the
 * horizontal padding and the inner scrolling region, and centres a max-width
 * column with a consistent vertical rhythm.
 */
const Page = React.forwardRef<HTMLDivElement, PageProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("flex min-h-0 flex-1 flex-col bg-background", className)}
        {...props}
      />
    );
  },
);
Page.displayName = "Page";

const PageHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & {
  width?: "narrow" | "default" | "wide";
}>(
  ({ className, width = "wide", ...props }, ref) => {
    const maxWidth =
      width === "narrow"
        ? "max-w-3xl"
        : width === "wide"
          ? "max-w-[88rem]"
          : "max-w-6xl";
    return (
      <div
        ref={ref}
        className={cn(
          "shrink-0 border-b bg-card py-4 md:py-5",
          className,
        )}
        {...props}
      >
        <div
          className={cn(
            "mx-auto flex w-full flex-col gap-4 px-4 md:px-8",
            maxWidth,
          )}
        >
          {props.children}
        </div>
      </div>
    );
  },
);
PageHeader.displayName = "PageHeader";

const PageBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & {
  width?: "narrow" | "default" | "wide";
}>(
  ({ className, width = "default", children, ...props }, ref) => {
    const maxWidth =
      width === "narrow"
        ? "max-w-3xl"
        : width === "wide"
          ? "max-w-[88rem]"
          : "max-w-6xl";
    return (
      <div
        ref={ref}
        className={cn("min-h-0 flex-1 overflow-y-auto", className)}
        {...props}
      >
        <div
          className={cn(
            "mx-auto flex w-full flex-col gap-6 px-4 py-6 md:gap-8 md:px-8 md:py-8",
            maxWidth,
          )}
        >
          {children}
        </div>
      </div>
    );
  },
);
PageBody.displayName = "PageBody";

const PageTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h1
      ref={ref}
      className={cn(
        "text-2xl font-semibold tracking-tight text-foreground",
        className,
      )}
      {...props}
    />
  ),
);
PageTitle.displayName = "PageTitle";

const PageDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
PageDescription.displayName = "PageDescription";

export {
  Page,
  PageHeader,
  PageBody,
  PageTitle,
  PageDescription,
};